import { ActionType, ComputedOptionInstance } from '@swd_admin/cdc-viewer';
import { Button, Select, Tooltip } from 'antd';
import SummerwoodSVG, {
  svgType,
} from 'components/summerwoodsvg/summerwood-svg';
import { motion, MotionStyle } from 'framer-motion';
import { getFileFormattedTime } from 'helpers/util';
import React, {  useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewerState } from 'state/viewer-state/viewer-state';
import { useSmallScreen } from 'helpers/isSmallScreen';
import { useAppState } from 'state/appstate/appstate';
import { EnvironmentTypeAdapter } from 'screens/viewer/viewer-screen';
import { ROUTES } from 'helpers/constants';
import { useLocation } from 'react-router';
import { defaultConfiguration } from 'viewer/api';
import {  DefaultBackground } from 'api';
import Typography from 'helpers/fonts';
import { showRenderResult, useUiState } from 'state/ui-state/ui-state';
import { ViewerConfirmDeleteMessage } from 'components/viewer-confirm-delete-message';

export type ViewerControlType =
  | 'reload'
  | 'resetCamera'
  | 'showDimensions'
  | 'takeScreenshot';

const ControlsStyle: MotionStyle = {
  position: 'absolute',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  transform: 'translateY(-50%)',
};

const ControlsInitialFrame = { translateY: '150%', opacity: 0 };
const ControlsAnimateFrame = {
  translateY: '-50%',
  opacity: 1,
  transition: { duration: 0.4 },
};

const ViewerControlButtons: React.FunctionComponent<{
  bottomSheetHeight: number;
}> = ({ bottomSheetHeight }) => {
  const isSmallScreen = useSmallScreen();
  const { pathname } = useLocation();
  const { viewer, viewerState } = useViewerState();
  const { setDefaultBackground, uState } = useAppState();
  const {
    options,
    features,
  } = useAppState((state) => ({
    options: state.styleProduct.style?.options,
    features: state.styleProduct.style?.features,
  }));
  const { bom } = useUiState((state) => ({
    bom: state.bom,
  }));
  const envs = [
    { label: 'Cabin', value: DefaultBackground.CABIN },
    { label: 'Garden', value: DefaultBackground.GARDEN },
    { label: 'Pool', value: DefaultBackground.POOL },
  ];
  const [dimensionLinesEnabled, setDimensionLinesEnabled] = useState<boolean>(
    !!viewerState?.dimensionLinesEnabled
  );
  const [isModalOpen,setIsModalOpen]=useState<boolean>(false)
  
  const changeScenery = (environment: DefaultBackground) => {
    viewer?.dispatch({
      type: ActionType.ChangeEnvironment,
      environmentType: EnvironmentTypeAdapter[environment],
    });
    setDefaultBackground(environment);
  };

  const alignEvenly = () => {
    viewer?.dispatch({
      type: ActionType.DistributeEvenly,
    });
  };

  const viewerCallbacks = {
    reload: defaultConfiguration,
    resetCamera: () => {
      viewer?.resetCamera();
    },
    showDimensions: () => {
      viewer?.dispatch({ type: ActionType.ToggleDimensionLines });
      setDimensionLinesEnabled(!dimensionLinesEnabled);
    },
    takeScreenshot: () => {
      viewer?.downloadScreenshot(getFileFormattedTime());
    },
  };
  const { t } = useTranslation();
  const controlButtons: { icon: svgType; type: ViewerControlType }[] = [
    { icon: 'reload', type: 'reload' },
    { icon: 'toolContain', type: 'resetCamera' },
    { icon: 'toolRuler', type: 'showDimensions' },
    { icon: 'toolCamera', type: 'takeScreenshot' },
  ];
  const viewerStateInternalInstances: ComputedOptionInstance[] = JSON.parse(localStorage.getItem('internal-computedInstances') ?? '[]');
  const deleteWindowsAndDoors = () => {
      if(viewerStateInternalInstances && viewerStateInternalInstances.length) {
        const instanceIds = viewerStateInternalInstances.map(option => option?.id);
        viewer?.dispatch({
          type: ActionType.DeleteMultipleOptions,
          instanceIds: instanceIds as string[]
        });
        showRenderResult();
      } 
      setIsModalOpen(false)
    }


  return (
    <>
      <motion.div
        style={{
          ...ControlsStyle,
          right: '6px',
          top: `calc(50% - ${bottomSheetHeight / 2}px)`,
        }}
        initial={ControlsInitialFrame}
        animate={ControlsAnimateFrame}
      >
        {controlButtons.map(({ icon, type }, index) => (
          <Tooltip
            placement="right"
            title={t(`viewerControls.${type}`)}
            key={type}
          >
            <Button
              style={{
                marginTop: index !== 0 ? '12px' : undefined,
                borderColor:
                  icon === 'toolRuler' && dimensionLinesEnabled
                    ? Theme['@success-color']
                    : undefined,
              }}
              icon={
                <SummerwoodSVG
                  type={icon}
                  style={{ height: '25px' }}
                  color={
                    icon === 'toolRuler' && dimensionLinesEnabled
                      ? Theme['@success-color']
                      : ''
                  }
                />
              }
              onClick={() => {
                // HACKY WAY TO RESET THE ACTIVE CLASS
                (document as any).activeElement &&
                  (document as any).activeElement.blur();

                viewerCallbacks[type]();
              }}
            />
          </Tooltip>
        ))}
      </motion.div>
      <motion.div
        style={{
          ...ControlsStyle,
          right: isSmallScreen ? 6 : 16,
          bottom: isSmallScreen ? 'initial' : 16,
          top: isSmallScreen
            ? `calc(100% - ${bottomSheetHeight + 24}px`
            : 'initial',
        }}
        initial={ControlsInitialFrame}
        animate={{ ...ControlsAnimateFrame, translateX: '50%', right: '50%' }}
      >
        <div style={{ display: 'flex' }}>
          <Select
            style={{ width: 150 }}
            value={uState?.background}
            onSelect={(value) => changeScenery(value as DefaultBackground)}
          >
            {envs.map((o) => (
              <Select.Option key={o.label} value={o.value}>
                <Typography styleid={6}>{o.label}</Typography>
              </Select.Option>
            ))}
          </Select>
          {pathname === ROUTES.OPTIONS_SCREEN && <div>
            <Button onClick={alignEvenly} style={{ marginLeft: '1rem' }}>
              {t(`viewerControls.alignEvenly`)}
            </Button>
            </div>
          }
          <Button disabled={!!!(viewerStateInternalInstances && viewerStateInternalInstances.length)} onClick={() => setIsModalOpen(true)} style={{ marginLeft: '1rem' }}>
              {t(`viewerControls.deleteDoorsAndWindows`)}
          </Button>
        </div>
      </motion.div>
      <ViewerConfirmDeleteMessage isOpen={isModalOpen} setIsOpen={setIsModalOpen} onConfirm={deleteWindowsAndDoors} />
    </>
  );
};

export default ViewerControlButtons;
